/**
 * 存储工厂 — 运行时检测选择实现
 *
 * 优先级：FS API（Chrome/Edge 桌面）→ IndexedDB（兜底）
 *
 * 对齐 architecture.md §五「统一存储抽象层」
 */

import type { Storage } from './index';
import { IndexedDbStorage } from './indexed-db';
import { createFsApiStorage } from './fs-api';

let currentStorage: Storage | null = null;

/** FS API handle 持久化 key（IndexedDB 存 handle） */
const HANDLE_DB_KEY = 'hump-fs-handle';

/** 检测 FS API 是否可用 */
export function hasFileSystemAccessAPI(): boolean {
  return 'showDirectoryPicker' in window;
}

/** 从 IndexedDB 恢复已保存的目录 handle */
async function getSavedHandle(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const { get: idbGet } = await import('idb-keyval');
    return (await idbGet(HANDLE_DB_KEY)) ?? null;
  } catch {
    return null;
  }
}

/** 持久化目录 handle（跨会话保留，需用户授权） */
async function saveHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  const { set: idbSet } = await import('idb-keyval');
  await idbSet(HANDLE_DB_KEY, handle);
}

/**
 * 初始化存储
 *
 * 1. 尝试恢复已保存的 FS API handle → 用 FsApiStorage
 * 2. 无 handle 或 FS API 不可用 → 降级 IndexedDbStorage
 *
 * 用户首次通过引导选择目录时调用 pickDirectory()。
 */
export async function initStorage(): Promise<Storage> {
  if (hasFileSystemAccessAPI()) {
    const handle = await getSavedHandle();
    if (handle) {
      const perm = await handle.queryPermission({ mode: 'readwrite' });
      if (perm === 'granted') {
        currentStorage = createFsApiStorage(handle);
        return currentStorage;
      }
    }
  }

  // 兜底
  currentStorage = IndexedDbStorage;
  return currentStorage;
}

/** 用户选择 Hump 根目录（首次引导） */
export async function pickDirectory(): Promise<Storage> {
  if (!hasFileSystemAccessAPI()) {
    currentStorage = IndexedDbStorage;
    return currentStorage;
  }

  const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
  await handle.requestPermission({ mode: 'readwrite' });
  await saveHandle(handle);
  currentStorage = createFsApiStorage(handle);
  return currentStorage;
}

/** 获取当前 storage 实例（必须先 initStorage） */
export function getStorage(): Storage {
  if (!currentStorage) {
    throw new Error('Storage not initialized. Call initStorage() first.');
  }
  return currentStorage;
}
